const FUNCTIONS_BASE = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

const post = (fn: string, payload: unknown) => {
  const url = `${FUNCTIONS_BASE}/${fn}`;
  const body = JSON.stringify(payload);
  try {
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body,
    });
  } catch {
    /* telemetry must never break the page */
  }
};

export type SiteEvent =
  | "demo_cta_click"
  | "demo_scheduled"
  | "contact_email_click"
  | "contact_call_click"
  | "page_view";

export const trackEvent = (
  event_name: SiteEvent,
  label?: string,
  props?: Record<string, unknown>,
) => {
  post("track-event", {
    event_name,
    label,
    props,
    page_path: typeof location !== "undefined" ? location.pathname + location.hash : null,
    referrer: typeof document !== "undefined" ? document.referrer : null,
  });
};

/** Reports CSP / Trusted Types violations from real sessions. */
export const initCspReporting = () => {
  if (typeof document === "undefined") return;
  document.addEventListener("securitypolicyviolation", (e) => {
    post("csp-report", {
      "document-uri": e.documentURI,
      "blocked-uri": e.blockedURI,
      "violated-directive": e.violatedDirective,
      "effective-directive": e.effectiveDirective,
      disposition: e.disposition,
      "source-file": e.sourceFile,
      "line-number": e.lineNumber,
      "column-number": e.columnNumber,
      "script-sample": e.sample,
    });
  });
};

const DEMO_CLICK_KEY = "demo_cta_click_at";

/** Track a Schedule-a-Demo click and remember it for conversion measurement. */
export const trackDemoClick = (location: string) => {
  try {
    sessionStorage.setItem(DEMO_CLICK_KEY, String(Date.now()));
  } catch {
    /* ignore */
  }
  trackEvent("demo_cta_click", location);
};

/**
 * Records a successful demo booking. Fires when the scheduler redirects back
 * with ?demo=scheduled, or when the visitor returns to the tab after spending
 * long enough in the scheduler to complete a booking.
 */
export const initDemoConversionTracking = () => {
  if (typeof window === "undefined") return;

  const params = new URLSearchParams(window.location.search);
  if (params.get("demo") === "scheduled") {
    trackEvent("demo_scheduled", "redirect_confirmed");
    try {
      sessionStorage.removeItem(DEMO_CLICK_KEY);
    } catch {
      /* ignore */
    }
    return;
  }

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible") return;
    try {
      const at = Number(sessionStorage.getItem(DEMO_CLICK_KEY));
      if (!at) return;
      const elapsed = Date.now() - at;
      sessionStorage.removeItem(DEMO_CLICK_KEY);
      if (elapsed > 45_000 && elapsed < 30 * 60_000) {
        trackEvent("demo_scheduled", "return_after_scheduler", { seconds_away: Math.round(elapsed / 1000) });
      }
    } catch {
      /* ignore */
    }
  });
};
